import { useMemo } from 'react';
import { toNumber } from '../../charts/utils/chartDataShaping';
import { parseCellKey } from './useCellSelection';
import type { CellAggregateValues } from './useCellSelection';

type NormalizedColumn = { id: string; key: string };

type UseCellAggregatesParams = {
  selectedCells: Set<string>;
  sortedResults: Record<string, unknown>[];
  columns: NormalizedColumn[];
  /** When rows start at an offset (e.g., windowed buffer), subtract this from absolute row indices */
  rowOffset?: number;
};

export function useCellAggregates({
  selectedCells,
  sortedResults,
  columns,
  rowOffset = 0,
}: UseCellAggregatesParams): CellAggregateValues | null {
  return useMemo(() => {
    if (selectedCells.size === 0) return null;

    // Build a quick lookup from column id → key (for row data access)
    const colKeyMap = new Map<string, string>();
    for (const col of columns) {
      colKeyMap.set(col.id, col.key);
    }

    const numericValues: number[] = [];
    let count = 0;

    for (const cellId of selectedCells) {
      const { rowIndex, columnId } = parseCellKey(cellId);
      const row = sortedResults[rowIndex - rowOffset];
      if (!row) continue;

      const colKey = colKeyMap.get(columnId);
      if (!colKey) continue;

      count++;
      const raw = row[colKey];
      const num = toNumber(raw);
      if (num !== null) numericValues.push(num);
    }

    if (count === 0) return null;

    if (numericValues.length === 0) {
      return {
        count,
        numericCount: 0,
        sum: null,
        avg: null,
        min: null,
        max: null,
      };
    }

    let sum = 0;
    let min = numericValues[0];
    let max = numericValues[0];
    for (const v of numericValues) {
      sum += v;
      if (v < min) min = v;
      if (v > max) max = v;
    }

    return {
      count,
      numericCount: numericValues.length,
      sum,
      avg: sum / numericValues.length,
      min,
      max,
    };
  }, [selectedCells, sortedResults, columns, rowOffset]);
}
